import { flow, types } from "mobx-state-tree";
import { ErrorModel } from "../../components/common/error";
import { MessageModel } from "../../components/common/message";
import { IInput } from "../../utils/common-interface";
import customtypes from "../../utils/mobx-types-helper";
import { PaginationModel } from "../share/PagingModel";
import LogsAPI from "./LogService";
import moment from "moment";

// ProjectLog Model
export const ProjectLogModel = types
  .model("ProjectLogModel", {
    id: types.identifierNumber,
    uid: types.optional(types.string, ""),
    userName: types.optional(types.string, ""),
    logType: types.optional(types.string, ""),
    ip: types.optional(types.string, ""),
    dateTime: customtypes.optional(types.string, ""),
    projectId: types.number,
    projectCode: types.optional(types.string, ""),
    projectName: types.optional(types.string, ""),
    organizationName: types.optional(types.string, ""),
    dataSummary: types.optional(types.string, ""),
    formattedDateTime: types.optional(types.string, ""),
  })
  .views((self: any) => ({
    get logTypeLabel() {
      const labels: { [key: string]: string } = {
        UP: "แก้ไข",
        AP: "เพิ่มแผน",
        DP: "ลบแผน",
        AJ: "เพิ่มโครงการ",
        DJ: "ลบโครงการ",
      };
      return labels[self.logType] || self.logType;
    },
  }));

export const PlanLogModel = types.model("PlanLogModel", {
  id: types.identifierNumber,
  uid: types.string,
  userName: types.maybeNull(types.string),
  logType: types.string,
  ip: types.string,
  dateTime: types.string,
  formattedDateTime: types.maybeNull(types.string),
  planId: types.number,
  planCode: types.maybeNull(types.string),
  planType: types.maybeNull(types.string),
  planTypeCode: types.maybeNull(types.string),
  planName: types.maybeNull(types.string),
  organizationName: types.maybeNull(types.string),
  dataSummary: types.maybeNull(types.string),
});

export const LogListModel = types
  .model("LogListModel", {
    list: customtypes.optional(types.array(ProjectLogModel), []),
    paging: customtypes.optional(PaginationModel, {
      pageSize: 10,
      pageNo: 1,
      sortBy: {
        columnName: "datetime",
        orderByDescending: true,
      },
    }),
    // Filters
    projectId: types.maybeNull(types.number),
    organizationId: types.maybeNull(types.number),
    logTypes: types.optional(types.array(types.string), []),
    fromDate: types.maybeNull(types.string),
    toDate: types.maybeNull(types.string),
    // UI state
    selectedItems: types.optional(types.array(types.identifierNumber), []),
    loading: types.optional(types.boolean, false),
    error: customtypes.optional(ErrorModel, {}),
    alert: customtypes.optional(MessageModel, {}),
  })
  .views((self: any) => ({
    get totalPages() {
      return Math.ceil(self.paging.totalRow / self.paging.pageSize);
    },
    get hasSelectedItems() {
      return self.selectedItems.length > 0;
    },
    get selectedCount() {
      return self.selectedItems.length;
    },
    get isAllSelected() {
      return (
        self.list.length > 0 && self.selectedItems.length === self.list.length
      );
    },
    get availableLogTypes() {
      return [
        { key: "UP", text: "แก้ไข", value: "UP" },
        { key: "AR", text: "จัดเก็บโครงการ", value: "AR" },
        { key: "UA", text: "ยกเลิกจัดเก็บโครงการ", value: "UA" },
      ];
    },
  }))
  .actions((self: any) => ({
    setField: ({ fieldname, value }: IInput) => {
      self[fieldname] = value;
    },
    setAllField: (data: any) => {
      Object.keys(data).forEach((key) => {
        try {
          self[key] = data[key];
        } catch (e) {
          console.log("exception on key", key, e);
          throw e;
        }
      });
    },
    resetAll: () => {
      self.list.clear();
      self.paging = {
        pageSize: 10,
        pageNo: 1,
        sortBy: {
          columnName: "datetime",
          orderByDescending: true,
        },
      };
      self.projectId = null;
      self.organizationId = null;
      self.logTypes.clear();
      self.fromDate = null;
      self.toDate = null;
      self.selectedItems.clear();
    },
    resetFilter: () => {
      self.projectId = null;
      self.organizationId = null;
      self.logTypes.clear();
      self.fromDate = null;
      self.toDate = null;
      self.paging.searchText = "";
      self.paging.pageNo = 1;
    },
    // Log type management
    toggleLogType: (logType: string) => {
      const index = self.logTypes.indexOf(logType);
      if (index > -1) {
        self.logTypes.splice(index, 1);
      } else {
        self.logTypes.push(logType);
      }
    },
    setLogTypes: (logTypes: string[]) => {
      self.logTypes.clear();
      logTypes.forEach((type) => self.logTypes.push(type));
    },
    // Date range helpers
    setDateRange: (period: string) => {
      const today = moment();
      let fromDate = "";
      let toDate = moment().format("YYYY-MM-DD");

      switch (period) {
        case "today":
          fromDate = moment().format("YYYY-MM-DD");
          break;
        case "yesterday":
          fromDate = moment().subtract(1, "days").format("YYYY-MM-DD");
          toDate = moment().subtract(1, "days").format("YYYY-MM-DD");
          break;
        case "week":
          fromDate = moment().startOf("week").format("YYYY-MM-DD");
          break;
        case "month":
          fromDate = moment().startOf("month").format("YYYY-MM-DD");
          break;
        case "lastMonth":
          fromDate = moment()
            .subtract(1, "month")
            .startOf("month")
            .format("YYYY-MM-DD");
          toDate = moment()
            .subtract(1, "month")
            .endOf("month")
            .format("YYYY-MM-DD");
          break;
        case "all":
          fromDate = "";
          toDate = "";
          break;
      }

      self.fromDate = fromDate;
      self.toDate = toDate;
    },
    // Selection management
    toggleSelectItem: (logId: number) => {
      const index = self.selectedItems.indexOf(logId);
      if (index === -1) {
        self.selectedItems.push(logId);
      } else {
        self.selectedItems.splice(index, 1);
      }
    },
    selectAllItems: () => {
      self.selectedItems.clear();
      self.list.forEach((log: any) => {
        self.selectedItems.push(log.id);
      });
    },
    clearSelectedItems: () => {
      self.selectedItems.clear();
    },
    toggleAllItems: () => {
      if (self.isAllSelected) {
        self.clearSelectedItems();
      } else {
        self.selectAllItems();
      }
    },
    // Sorting
    setSortBy: (columnName: string) => {
      if (self.paging.sortBy.columnName === columnName) {
        self.paging.sortBy.orderByDescending =
          !self.paging.sortBy.orderByDescending;
      } else {
        self.paging.sortBy.columnName = columnName;
        self.paging.sortBy.orderByDescending = true;
      }
    },
    // Search
    onSearchTextFilter: (value: string) => {
      self.setDefaultPagination();
      self.paging.searchText = value;
      self.getProjectLogsList();
    },
    // Pagination
    setPagination: (result: any) => {
      self.paging.pageSize = result.pageSize;
      self.paging.pageNo = result.pageNo;
      self.paging.totalRow = result.totalRow;
    },
    onSelectedPage: (pageNo: number) => {
      self.paging.pageNo = pageNo;
      self.getProjectLogsList();
    },
    setDefaultPagination: () => {
      self.paging.pageNo = 1;
    },
    // Main API calls
    getProjectLogsList: flow(function* () {
      try {
        self.loading = true;
        self.error.setField({ fieldname: "tigger", value: false });

        // Build request payload
        const requestPayload = {
          projectId: self.projectId || null,
          organizationId: self.organizationId || null,
          logTypes: self.logTypes.length > 0 ? self.logTypes.slice() : null,
          fromDate: self.fromDate || null,
          toDate: self.toDate || null,
          paging: {
            pageNo: self.paging.pageNo,
            pageSize: self.paging.pageSize,
            searchText: self.paging.searchText || null,
            sortBy: {
              columnName: self.paging.sortBy.columnName,
              orderByDescending: self.paging.sortBy.orderByDescending,
            },
          },
        };

        const result = yield LogsAPI.getProjectLogsList(requestPayload);

        if (result && result.data) {
          // Clear and populate list
          self.list.clear();
          result.data.data.forEach((log: any) => {
            self.list.push(log);
          });

          // Update pagination info
          self.setPagination({
            pageSize: result.data.pageSize,
            pageNo: result.data.pageNo,
            totalRow: result.data.totalRow,
          });
        }
      } catch (e: any) {
        console.error("Error fetching project logs:", e);
        self.error.tigger = true;
        self.error.code = e.code;
        self.error.title = e.name || "Error";
        self.error.message = e.message || "ไม่สามารถโหลดข้อมูล Logs ได้";
        self.error.technicalStack = e.technicalStack;
        throw e;
      } finally {
        self.loading = false;
      }
    }),
    // exportProjectLogs: flow(function* (format: string = "excel") {
    //   try {
    //     self.loading = true;
    //     self.error.setField({ fieldname: "tigger", value: false });

    //     // Build request payload for export
    //     const requestPayload = {
    //       projectId: self.projectId || null,
    //       organizationId: self.organizationId || null,
    //       logTypes: self.logTypes.length > 0 ? self.logTypes.slice() : null,
    //       fromDate: self.fromDate || null,
    //       toDate: self.toDate || null,
    //       format: format, // excel, csv, pdf
    //       paging: {
    //         pageNo: 1, // Export all
    //         pageSize: 999999, // Large number to get all records
    //         searchText: self.paging.searchText || null,
    //         sortBy: {
    //           columnName: self.paging.sortBy.columnName,
    //           orderByDescending: self.paging.sortBy.orderByDescending,
    //         },
    //       },
    //     };

    //     const result = yield LogsAPI.exportProjectLogs(requestPayload);

    //     if (result && result.data) {
    //       // Create download link
    //       const blob = new Blob([result.data], {
    //         type: format === "excel"
    //           ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    //           : format === "csv"
    //           ? "text/csv"
    //           : "application/pdf"
    //       });
    //       const url = window.URL.createObjectURL(blob);
    //       const link = document.createElement("a");
    //       const filename = `project_logs_${moment().format("YYYYMMDD_HHmmss")}.${format === "excel" ? "xlsx" : format}`;

    //       link.href = url;
    //       link.download = filename;
    //       document.body.appendChild(link);
    //       link.click();

    //       // Cleanup
    //       setTimeout(() => {
    //         document.body.removeChild(link);
    //         window.URL.revokeObjectURL(url);
    //       }, 100);

    //       self.alert.tigger = true;
    //       self.alert.title = "ส่งออกข้อมูลสำเร็จ";
    //       self.alert.message = `ส่งออกข้อมูล Project Logs เรียบร้อยแล้ว`;
    //     }
    //   } catch (e: any) {
    //     console.error("Error exporting project logs:", e);
    //     self.error.tigger = true;
    //     self.error.code = e.code;
    //     self.error.title = e.name || "Error";
    //     self.error.message = e.message || "ไม่สามารถส่งออกข้อมูล Logs ได้";
    //     self.error.technicalStack = e.technicalStack;
    //     throw e;
    //   } finally {
    //     self.loading = false;
    //   }
    // }),
  }));

export type ILogListModel = typeof LogListModel.Type;

export const PlanLogListModel = types
  .model("PlanLogListModel", {
    list: customtypes.optional(types.array(PlanLogModel), []),
    paging: customtypes.optional(PaginationModel, {
      pageSize: 10,
      pageNo: 1,
      sortBy: {
        columnName: "datetime",
        orderByDescending: true,
      },
    }),
    planId: types.maybeNull(types.number),
    organizationId: types.maybeNull(types.number),
    logTypes: types.optional(types.array(types.string), []),
    fromDate: types.maybeNull(types.string),
    toDate: types.maybeNull(types.string),
    selectedItems: types.optional(types.array(types.identifierNumber), []),
    loading: types.optional(types.boolean, false),
    error: customtypes.optional(ErrorModel, {}),
    alert: customtypes.optional(MessageModel, {}),
  })
  .views((self: any) => ({
    get totalPages() {
      return Math.ceil(self.paging.totalRow / self.paging.pageSize);
    },
    get hasSelectedItems() {
      return self.selectedItems.length > 0;
    },
    get selectedCount() {
      return self.selectedItems.length;
    },
    get isAllSelected() {
      return (
        self.list.length > 0 && self.selectedItems.length === self.list.length
      );
    },
    get availableLogTypes() {
      return [
        { key: "UP", text: "แก้ไข", value: "UP" },
        { key: "DP", text: "ลบแผน", value: "DP" },
        { key: "AP", text: "เพิ่มแผน", value: "AP" },
        { key: "AJ", text: "เพิ่มโครงการ", value: "AJ" },
        { key: "DJ", text: "ลบโครงการ", value: "DJ" },
      ];
    },
  }))
  .actions((self: any) => ({
    setField: ({ fieldname, value }: IInput) => {
      self[fieldname] = value;
    },
    setAllField: (data: any) => {
      Object.keys(data).forEach((key) => {
        try {
          self[key] = data[key];
        } catch (e) {
          console.log("exception on key", key, e);
          throw e;
        }
      });
    },
    resetAll: () => {
      self.list.clear();
      self.paging = {
        pageSize: 10,
        pageNo: 1,
        sortBy: {
          columnName: "datetime",
          orderByDescending: true,
        },
      };
      self.projectId = null;
      self.organizationId = null;
      self.logTypes.clear();
      self.fromDate = null;
      self.toDate = null;
      self.selectedItems.clear();
    },
    resetFilter: () => {
      self.projectId = null;
      self.organizationId = null;
      self.logTypes.clear();
      self.fromDate = null;
      self.toDate = null;
      self.paging.searchText = "";
      self.paging.pageNo = 1;
    },
    // Log type management
    toggleLogType: (logType: string) => {
      const index = self.logTypes.indexOf(logType);
      if (index > -1) {
        self.logTypes.splice(index, 1);
      } else {
        self.logTypes.push(logType);
      }
    },
    setLogTypes: (logTypes: string[]) => {
      self.logTypes.clear();
      logTypes.forEach((type) => self.logTypes.push(type));
    },
    // Date range helpers
    setDateRange: (period: string) => {
      const today = moment();
      let fromDate = "";
      let toDate = moment().format("YYYY-MM-DD");

      switch (period) {
        case "today":
          fromDate = moment().format("YYYY-MM-DD");
          break;
        case "yesterday":
          fromDate = moment().subtract(1, "days").format("YYYY-MM-DD");
          toDate = moment().subtract(1, "days").format("YYYY-MM-DD");
          break;
        case "week":
          fromDate = moment().startOf("week").format("YYYY-MM-DD");
          break;
        case "month":
          fromDate = moment().startOf("month").format("YYYY-MM-DD");
          break;
        case "lastMonth":
          fromDate = moment()
            .subtract(1, "month")
            .startOf("month")
            .format("YYYY-MM-DD");
          toDate = moment()
            .subtract(1, "month")
            .endOf("month")
            .format("YYYY-MM-DD");
          break;
        case "all":
          fromDate = "";
          toDate = "";
          break;
      }

      self.fromDate = fromDate;
      self.toDate = toDate;
    },
    // Selection management
    toggleSelectItem: (logId: number) => {
      const index = self.selectedItems.indexOf(logId);
      if (index === -1) {
        self.selectedItems.push(logId);
      } else {
        self.selectedItems.splice(index, 1);
      }
    },
    selectAllItems: () => {
      self.selectedItems.clear();
      self.list.forEach((log: any) => {
        self.selectedItems.push(log.id);
      });
    },
    clearSelectedItems: () => {
      self.selectedItems.clear();
    },
    toggleAllItems: () => {
      if (self.isAllSelected) {
        self.clearSelectedItems();
      } else {
        self.selectAllItems();
      }
    },
    // Sorting
    setSortBy: (columnName: string) => {
      if (self.paging.sortBy.columnName === columnName) {
        self.paging.sortBy.orderByDescending =
          !self.paging.sortBy.orderByDescending;
      } else {
        self.paging.sortBy.columnName = columnName;
        self.paging.sortBy.orderByDescending = true;
      }
    },
    // Search
    onSearchTextFilter: (value: string) => {
      self.setDefaultPagination();
      self.paging.searchText = value;
      self.getPlanLogsList();
    },
    // Pagination
    setPagination: (result: any) => {
      self.paging.pageSize = result.pageSize;
      self.paging.pageNo = result.pageNo;
      self.paging.totalRow = result.totalRow;
    },
    onSelectedPage: (pageNo: number) => {
      self.paging.pageNo = pageNo;
      self.getPlanLogsList();
    },
    setDefaultPagination: () => {
      self.paging.pageNo = 1;
    },
    getPlanLogsList: flow(function* () {
      try {
        self.loading = true;
        self.error.setField({ fieldname: "tigger", value: false });

        // Build request payload
        const requestPayload = {
          planId: self.planId || null,
          organizationId: self.organizationId || null,
          logTypes: self.logTypes.length > 0 ? self.logTypes.slice() : null,
          fromDate: self.fromDate || null,
          toDate: self.toDate || null,
          paging: {
            pageNo: self.paging.pageNo,
            pageSize: self.paging.pageSize,
            searchText: self.paging.searchText || null,
            sortBy: {
              columnName: self.paging.sortBy.columnName,
              orderByDescending: self.paging.sortBy.orderByDescending,
            },
          },
        };

        const result = yield LogsAPI.getPlanLogsList(requestPayload);

        if (result && result.data) {
          // Clear and populate list
          self.list.clear();
          result.data.data.forEach((log: any) => {
            self.list.push(log);
          });

          // Update pagination info
          self.setPagination({
            pageSize: result.data.pageSize,
            pageNo: result.data.pageNo,
            totalRow: result.data.totalRow,
          });
        }
      } catch (e: any) {
        console.error("Error fetching project logs:", e);
        self.error.tigger = true;
        self.error.code = e.code;
        self.error.title = e.name || "Error";
        self.error.message = e.message || "ไม่สามารถโหลดข้อมูล Logs ได้";
        self.error.technicalStack = e.technicalStack;
        throw e;
      } finally {
        self.loading = false;
      }
    }),
  }));
export type IPlanLogListModel = typeof PlanLogListModel.Type;
